import React, { useState, useEffect } from 'react';
import api from '../api';
import { format, parseISO, isToday, startOfWeek, endOfWeek } from 'date-fns';
import { Link } from 'react-router-dom';

const DirectorDashboard = () => {
  const [summary, setSummary] = useState([]);
  const [offToday, setOffToday] = useState([]);
  const [offThisWeek, setOffThisWeek] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    const load = async () => {
      try {
        const today = new Date();
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
        const todayStr = format(today, 'yyyy-MM-dd');
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

        const [sumRes, todayRes, weekRes, pendRes] = await Promise.all([
          api.get(`/api/users/summary?year=${year}`),
          api.get(`/api/holidays/calendar?from=${todayStr}&to=${todayStr}`),
          api.get(`/api/holidays/calendar?from=${weekStartStr}&to=${weekEndStr}`),
          api.get(`/api/holidays/all?status=pending&year=${year}`)
        ]);

        setSummary(sumRes.data);
        setOffToday(todayRes.data);
        setOffThisWeek(weekRes.data);
        setPending(pendRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const typeLabels = { holiday: 'Holiday', sick: 'Sick Day', awol: 'AWOL' };

  if (loading) return <div className="loader">Loading...</div>;

  const totalStaff = summary.filter(u => u.role === 'staff' || u.role === 'director').length;
  const staffWithEntitlement = summary.filter(u => u.holiday_entitlement > 0);
  const avgRemaining = staffWithEntitlement.length > 0
    ? Math.round(staffWithEntitlement.reduce((acc, u) => acc + (u.holiday_entitlement - parseFloat(u.holidays_taken)), 0) / staffWithEntitlement.length)
    : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Director Overview</h1>
          <p className="page-subtitle">{format(new Date(), 'EEEE, d MMMM yyyy')}</p>
        </div>
        <Link to="/manage-requests" className="btn btn-primary">
          Manage Requests {pending.length > 0 && <span style={{ background: 'var(--gold)', color: 'var(--navy)', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', marginLeft: '4px' }}>{pending.length}</span>}
        </Link>
      </div>

      <div className="stat-grid">
        <div className="stat-card gold-accent">
          <div className="stat-label">Off Today</div>
          <div className="stat-value">{offToday.length}</div>
          <div className="stat-sub">of {totalStaff} team members</div>
        </div>
        <div className="stat-card navy-accent">
          <div className="stat-label">Off This Week</div>
          <div className="stat-value">{offThisWeek.length}</div>
          <div className="stat-sub">approved absences</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Requests</div>
          <div className="stat-value">{pending.length}</div>
          <div className="stat-sub">awaiting decision</div>
        </div>
        <div className="stat-card success-accent">
          <div className="stat-label">Avg. Days Remaining</div>
          <div className="stat-value">{avgRemaining}</div>
          <div className="stat-sub">across entitled staff</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Off today */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Off Today</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {offToday.length === 0 ? (
              <div style={{ padding: '28px', textAlign: 'center', color: 'var(--grey-mid)', fontSize: '13px' }}>Everyone is in today</div>
            ) : (
              offToday.map(r => (
                <div key={r.id} style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{r.staff_name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{r.job_title || r.department || ''}</div>
                  </div>
                  <span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending requests */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pending Approval</h3>
            <Link to="/manage-requests" style={{ fontSize: '12px', color: 'var(--gold)', textDecoration: 'none' }}>Manage all</Link>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {pending.length === 0 ? (
              <div style={{ padding: '28px', textAlign: 'center', color: 'var(--grey-mid)', fontSize: '13px' }}>No pending requests</div>
            ) : (
              pending.slice(0, 5).map(r => (
                <div key={r.id} style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{r.staff_name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>
                      {format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'd MMM')} {r.start_date !== r.end_date ? `- ${format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'd MMM')}` : ''} &middot; {r.days_taken} day{r.days_taken !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Team entitlement summary */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Team Holiday Summary {year}</h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Entitlement</th>
                <th>Taken</th>
                <th>Sick Days</th>
                <th>AWOL</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(u => {
                const remaining = u.holiday_entitlement - parseFloat(u.holidays_taken);
                const lowAlert = u.holiday_entitlement > 0 && remaining <= 3;
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: '500' }}>{u.name}</td>
                    <td><span style={{ fontSize: '11px', color: 'var(--grey-mid)', textTransform: 'capitalize' }}>{u.role}</span></td>
                    <td>{u.holiday_entitlement > 0 ? `${u.holiday_entitlement} days` : <span style={{ color: 'var(--grey-mid)' }}>N/A</span>}</td>
                    <td>{u.holidays_taken}</td>
                    <td>{u.sick_days > 0 ? <span style={{ color: 'var(--warning)' }}>{u.sick_days}</span> : '0'}</td>
                    <td>{u.awol_days > 0 ? <span style={{ color: 'var(--danger)' }}>{u.awol_days}</span> : '0'}</td>
                    <td>
                      {u.holiday_entitlement > 0 ? (
                        <span style={{ color: lowAlert ? 'var(--danger)' : 'var(--success)', fontWeight: '500' }}>
                          {remaining}
                        </span>
                      ) : <span style={{ color: 'var(--grey-mid)' }}>N/A</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DirectorDashboard;
